/**
 * RegisterBefundActions - the exits of a Register-Befund lock on the Brokermandat (P5-S1, step 3).
 *
 * A Befund on the register check locks the CUSTOMER for the broker mandate until an admin clears it
 * with a reason code and a written reason (Valerio 22.10.). The exits:
 *   - «Freigeben» (tenant.manage): the reason dialog, then the subject-keyed clear. Works with or
 *     without a draft, so it is also offered after the Vorgang was aborted.
 *   - «Vorgang abbrechen» (mandate.dispatch, while the draft exists): the wizard cancel with the
 *     reason compliance. The Befund and its task stay; the clear stays the only way out.
 *
 * Mounted on the task detail of «Register-Befund Brokermandat prüfen» (RegisterBefundActions) and in
 * the customer header (RegisterBefundFlag: the red flag, read-only without tenant.manage). The api
 * decides the state; the web only renders it and gates each button on the key its route enforces.
 *
 * [Source: docs/product/p4-p6-slices/p5-S1-ausloesung-register-stub-mandatsumfang-dokument-design.md] (§1 step 3, [p5s1-17])
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import {
  MANDATE_REGISTER_CLEAR_REASON_VALUES,
  MANDATE_REGISTER_CLEAR_TEXT,
  REGISTER_BEFUND_ADMIN_PERMISSION,
  REGISTER_CHECK_SOURCE_LABEL,
  REGISTER_CLEAR_REASON_LABEL,
} from '@brokly/shared';
import type { MandateRegisterClearReason } from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { dateShortDe } from '../../lib/format';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';

interface SubjectProps {
  subjectType: 'person' | 'firma';
  subjectId: string;
}

/** The customer's Register-Befund state (shared by the task detail and the header). */
function useRegisterBefund({ subjectType, subjectId }: SubjectProps, enabled = true) {
  return useQuery({
    queryKey: qk.registerBefund(subjectType, subjectId),
    queryFn: () => apiClient.mandateBroker.customerRegisterBefund(subjectType, subjectId),
    enabled,
  });
}

/** Every key a lock change touches: the flag, the mandate tiles, the tasks. */
function useInvalidateLock({ subjectType, subjectId }: SubjectProps) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.registerBefund(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: qk.mandateBadges(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  };
}

/** The reason dialog of «Freigeben»: a code and a written reason (3+ characters). */
function ClearDialog({
  subjectType,
  subjectId,
  checkId,
  onClose,
}: SubjectProps & { checkId: string; onClose: () => void }) {
  const invalidate = useInvalidateLock({ subjectType, subjectId });
  const [reasonCode, setReasonCode] = useState<MandateRegisterClearReason | ''>('');
  const [reason, setReason] = useState('');
  const clear = useMutation({
    mutationFn: () =>
      apiClient.mandateBroker.clearRegisterBefund(subjectType, subjectId, checkId, {
        reasonCode: reasonCode as MandateRegisterClearReason,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const ready = reasonCode !== '' && reason.trim().length >= MANDATE_REGISTER_CLEAR_TEXT.min;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Register-Befund freigeben"
    >
      <div className="mx-4 flex w-full max-w-md flex-col gap-3 rounded-lg border bg-card p-5 shadow-lg">
        <h2 className="text-sm font-semibold">Register-Befund freigeben</h2>
        <p className="text-xs text-muted-foreground">
          Die Freigabe hebt die Sperre für das Brokermandat dieses Kunden auf. Die Begründung wird beim
          Befund gespeichert.
        </p>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium">Grund</span>
          <select
            className="rounded border bg-background px-2 py-1.5 text-sm"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value as MandateRegisterClearReason | '')}
            aria-label="Grund"
          >
            <option value="">Bitte wählen</option>
            {MANDATE_REGISTER_CLEAR_REASON_VALUES.map((code) => (
              <option key={code} value={code}>
                {REGISTER_CLEAR_REASON_LABEL[code]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium">Begründung</span>
          <textarea
            className="min-h-24 rounded border bg-background px-2 py-1.5 text-sm"
            value={reason}
            maxLength={MANDATE_REGISTER_CLEAR_TEXT.max}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Begründung"
          />
        </label>
        {clear.isError && (
          <p className="text-xs text-destructive">
            {clear.error instanceof Error ? clear.error.message : 'Die Freigabe ist fehlgeschlagen.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={clear.isPending}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={() => clear.mutate()} disabled={!ready || clear.isPending}>
            Freigeben
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The task detail of «Register-Befund Brokermandat prüfen»: Freigeben and Vorgang abbrechen. */
export function RegisterBefundActions({ subjectType, subjectId }: SubjectProps) {
  const { can } = useEffectivePermissions();
  const state = useRegisterBefund({ subjectType, subjectId });
  const invalidate = useInvalidateLock({ subjectType, subjectId });
  const [clearing, setClearing] = useState(false);
  const abort = useMutation({
    mutationFn: (mandateId: string) => apiClient.mandateWizard.cancel(mandateId, { reason: 'compliance' }),
    onSuccess: invalidate,
  });

  const befund = state.data?.befund ?? null;
  const draftId = state.data?.openDraftMandateId ?? null;
  if (befund === null) {
    return state.isSuccess ? (
      <p className="text-xs text-muted-foreground">Kein offener Register-Befund für diesen Kunden.</p>
    ) : null;
  }

  const detailsVisible = state.data?.detailsVisible === true;
  return (
    <div className="flex flex-col gap-2" data-testid="register-befund-actions">
      <p className="text-xs text-muted-foreground">
        {detailsVisible
          ? `Befund in: ${befund.befundSources.map((s) => REGISTER_CHECK_SOURCE_LABEL[s]).join(', ')}`
          : `Compliance-Befund offen seit ${dateShortDe(befund.checkedAt)}`}
        {draftId === null ? ' · Vorgang abgebrochen' : ''}
      </p>
      {!can(REGISTER_BEFUND_ADMIN_PERMISSION) && (
        <p className="text-xs text-muted-foreground" data-testid="register-befund-who">
          Freigeben kann nur eine Person mit dem Recht «Mandant verwalten», auf der Kundenseite oder hier.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {can(REGISTER_BEFUND_ADMIN_PERMISSION) && (
          <Button size="sm" onClick={() => setClearing(true)}>
            Freigeben
          </Button>
        )}
        {can('mandate.dispatch') && draftId !== null && (
          <Button size="sm" variant="outline" onClick={() => abort.mutate(draftId)} disabled={abort.isPending}>
            Vorgang abbrechen
          </Button>
        )}
      </div>
      {clearing && (
        <ClearDialog subjectType={subjectType} subjectId={subjectId} checkId={befund.id} onClose={() => setClearing(false)} />
      )}
    </div>
  );
}

/** The customer header's red flag «Register-Befund: gesperrt», with «Freigeben» for tenant.manage (never read-only). */
export function RegisterBefundFlag({ subjectType, subjectId, enabled = true, readOnly = false }: SubjectProps & { enabled?: boolean; readOnly?: boolean }) {
  const { can } = useEffectivePermissions();
  const state = useRegisterBefund({ subjectType, subjectId }, enabled);
  const [clearing, setClearing] = useState(false);
  const befund = state.data?.befund ?? null;
  if (befund === null) return null;

  return (
    <div className="flex items-center gap-1" data-testid="register-befund-flag">
      <Badge variant="alert">
        <ShieldAlert className="size-3" />
        Register-Befund: gesperrt
      </Badge>
      {/* Without tenant.manage only that a Compliance-Befund is open and since when, no sources. */}
      <span className="text-xs text-muted-foreground">
        {state.data?.detailsVisible === true
          ? befund.befundSources.map((s) => REGISTER_CHECK_SOURCE_LABEL[s]).join(', ')
          : `Compliance-Befund offen seit ${dateShortDe(befund.checkedAt)}`}
      </span>
      {can(REGISTER_BEFUND_ADMIN_PERMISSION) && !readOnly && (
        <Button size="sm" variant="outline" onClick={() => setClearing(true)}>
          Freigeben
        </Button>
      )}
      {clearing && (
        <ClearDialog subjectType={subjectType} subjectId={subjectId} checkId={befund.id} onClose={() => setClearing(false)} />
      )}
    </div>
  );
}
